import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open') && this.refs.details.classList.contains('menu-open');
  }

  /**
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault?.();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault?.();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);

    details.setAttribute('open', '');
    this.refs.menuDrawer.classList.remove('menu-drawer--closing');

    const panel =
      details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu') || this.refs.menuDrawer;
    void panel.offsetWidth;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        if (details === this.refs.details) {
          document.documentElement.setAttribute('scroll-lock', '');
        }

        if (target) {
          this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        }

        onAnimationEnd(panel, () => trapFocus(details), { subtree: false });
      });
    });
  }

  /**
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.add('menu-drawer--closing');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.refs.menuDrawer.classList.remove('menu-drawer--closing');
          document.documentElement.removeAttribute('scroll-lock');
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
